import { Item, LinkedItem, LinkedItemSon, SceneObjects } from '@types'
import { isHoodItem } from '@utils/kitchenInspector/classifyItem'
import isItem from '@utils/sceneObjects/assertions/isItem'
import isLinkedItem from '@utils/sceneObjects/assertions/isLinkedItem'
import isLinkedSon from '@utils/sceneObjects/assertions/isLinkedSon'
import {
  getEncimeraAcabadoThicknessMm,
  getEncimeraTextureEntry,
  getIndependentZocaloChildYLiftMm,
  isEncimeraLinkedSonItem,
  isFreeDimensionLateralOrCostadoCatalog,
  isGvMoldingUnit,
} from '@utils/sceneObjects/linked/freeDimensionLinkedCatalogs'
import { getObbCenter } from '@utils/sceneObjects/snappedSides'
import { Matrix4, Quaternion, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'

/** Alto máximo (mm) de un pequeño electrodoméstico de encimera. */
const MAX_COUNTERTOP_APPLIANCE_HEIGHT_MM = 550
/** Holgura (mm) de huella XZ para detectar solape en planta. */
const HOST_XZ_MARGIN_MM = 4
/** El sólido del mueble se recorta este tanto por arriba: sentarse en el tope no cuenta como penetrar. */
const SEAT_SINK_MM = 4
/** Holgura de contacto (mm): cara contra cara OK, interior no. */
const CONTACT_TOLERANCE_MM = 1
/** Grosor de encimera fuera de este rango se ignora. */
const MAX_ENCIMERA_THICKNESS_MM = 80

const SEATED_Y_TOLERANCE_MM = 30

const lastValidById = new Map<
  string,
  { x: number; y: number; z: number; quaternion: Quaternion; hostId?: string }
>()
const probeOBB = new OBB()
const neighborOBB = new OBB()
const probeCore = new OBB()
const neighborCore = new OBB()

type ItemWithGlbPivot = {
  freeDimensionGlbMinYMm?: number
  glbSize?: { y?: number }
}

export type SmallCountertopAppliance = Item | LinkedItem

function normalizeApplianceCode(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/^PE\s+/, '')
    .replace(/[^A-Z0-9]/g, '')
}

/** Micros de encimera del menú de pequeños (MICROTPC1, MICROTPC2, …). */
export function isCountertopMicroCatalog(item?: SceneObjects | null): boolean {
  if (!item) return false
  const fields = [
    'catalogID' in item ? item.catalogID : '',
    'codigo' in item ? (item as { codigo?: string | number }).codigo : '',
    'referencia' in item ? (item as { referencia?: string }).referencia : '',
    'description' in item ? item.description : '',
  ]
  return fields.some((field) => /^MICROTPC\d+$/.test(normalizeApplianceCode(field)))
}

function normalizeMenuClave(clave?: string | null): string {
  return String(clave ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
}

const DECORATION_SUBMENU_KEYS = new Set(['ILUMINACIONMENU', 'ESTANTERIASMENU', 'PLANTASMENU'])

function isDecorationMenuClave(clave?: string | null): boolean {
  const normalized = normalizeMenuClave(clave)
  if (!normalized) return false
  if (normalized === 'DECORACIONMENU' || normalized.startsWith('DECORACION')) return true
  return DECORATION_SUBMENU_KEYS.has(normalized)
}

/** Unidades de DECORACIÓNMENU (plantas, estanterías, lámparas objeto, etc.). */
export function isDecorationMenuUnit(item?: SceneObjects | null): boolean {
  if (!item) return false
  const principal = 'menuPrincipal' in item ? (item as { menuPrincipal?: string }).menuPrincipal : ''
  const clave = 'claveMenu' in item ? (item as { claveMenu?: string }).claveMenu : ''
  return isDecorationMenuClave(principal) || isDecorationMenuClave(clave)
}

/**
 * Pequeños electrodomésticos de encimera y el resto de objetos de colocación libre
 * (DECORACIÓNMENU y submenús: plantas, iluminación objeto, estanterías…).
 */
export function isSmallCountertopAppliance(
  item?: SceneObjects | null,
): item is SmallCountertopAppliance {
  if (!item || (!isItem(item) && !isLinkedItem(item))) return false
  if (isHoodItem(item)) return false
  if (isCountertopMicroCatalog(item) || isDecorationMenuUnit(item)) return true
  if (!isItem(item) || !item.universal) return false
  const tipo = String(item.tipoNormalizado || '').toUpperCase()
  if (tipo === 'MBJ' || tipo === 'MAL' || tipo === 'COL') return false
  return (item.height || 0) > 0 && (item.height || 0) <= MAX_COUNTERTOP_APPLIANCE_HEIGHT_MM
}

/** Mueble que puede hacer de apoyo / obstáculo sólido. */
export function isFurnitureHostUnit(item?: SceneObjects | null): item is LinkedItem {
  if (!item || !isLinkedItem(item) || item.visible === false) return false
  if (isSmallCountertopAppliance(item)) return false
  if (isGvMoldingUnit(item) || isFreeDimensionLateralOrCostadoCatalog(item)) return false
  const width = item.width || 0
  const thickness = item.thickness || 0
  return width >= 80 && thickness >= 80 && (item.height || 0) > 0
}

function findEncimeraChild(
  unit: LinkedItem,
  items: Map<string, SceneObjects>,
): LinkedItemSon | undefined {
  if (!unit.childs) return undefined
  for (const childId of unit.childs) {
    const child = items.get(childId)
    if (isLinkedSon(child) && isEncimeraLinkedSonItem(child)) return child
  }
  return undefined
}

function getEncimeraThicknessMm(encimera: LinkedItemSon): number {
  const fromStore = Number(encimera.height)
  if (Number.isFinite(fromStore) && fromStore > 0 && fromStore <= MAX_ENCIMERA_THICKNESS_MM) {
    return fromStore
  }
  const fromAcabado = getEncimeraAcabadoThicknessMm(getEncimeraTextureEntry(encimera)?.acabado)
  return fromAcabado ?? 0
}

/**
 * Tope de apoyo (mm mundo).
 * En KBB el store.height es el cuerpo (800); el zócalo hijo levanta el mesh 100 mm
 * sin tocar item.y. Hay que sumar ese lift. Si ya se aplicó el offset al suelo
 * (item.y += 100), no se vuelve a sumar.
 */
export function getUnitSupportTopYMm(
  unit: LinkedItem,
  items: Map<string, SceneObjects>,
): number {
  const floorOffsetApplied = Boolean(
    (unit as { independentZocaloFloorOffsetApplied?: boolean }).independentZocaloFloorOffsetApplied,
  )
  const zocaloLift = floorOffsetApplied ? 0 : getIndependentZocaloChildYLiftMm(unit)
  const bodyTop = (unit.y ?? 0) + (unit.height || 0) + zocaloLift
  const encimera = findEncimeraChild(unit, items)
  if (!encimera) return bodyTop
  return bodyTop + getEncimeraThicknessMm(encimera)
}

export function getApplianceVisualBaseOffsetYMm(appliance: SmallCountertopAppliance): number {
  const minYMm = Number((appliance as ItemWithGlbPivot).freeDimensionGlbMinYMm)
  if (!Number.isFinite(minYMm) || minYMm === 0) return 0
  const glbHeightM = Number((appliance as ItemWithGlbPivot).glbSize?.y)
  const glbHeightMm = Number.isFinite(glbHeightM) && glbHeightM > 0 ? glbHeightM * 1000 : 0
  const scaleY = glbHeightMm > 0 && appliance.height > 0 ? appliance.height / glbHeightMm : 1
  return minYMm * scaleY
}

export function getSeatedApplianceYMm(
  appliance: SmallCountertopAppliance,
  unit: LinkedItem,
  items: Map<string, SceneObjects>,
): number {
  return getUnitSupportTopYMm(unit, items) - getApplianceVisualBaseOffsetYMm(appliance)
}

function decomposeOrigin(matrix: { decompose: (p: Vector3, q: Quaternion, s: Vector3) => void }) {
  const position = new Vector3()
  const quaternion = new Quaternion()
  matrix.decompose(position, quaternion, new Vector3())
  return { position, quaternion }
}

function fillOBB(
  origin: Vector3,
  quaternion: Quaternion,
  width: number,
  height: number,
  thickness: number,
  target: OBB,
): OBB {
  target.center.copy(getObbCenter(origin, quaternion, width, height, thickness))
  target.halfSize.set(width / 2, height / 2, thickness / 2)
  target.rotation.setFromMatrix4(new Matrix4().makeRotationFromQuaternion(quaternion))
  return target
}

function shrinkUniform(source: OBB, tolMm: number, target: OBB): OBB {
  target.center.copy(source.center)
  target.rotation.copy(source.rotation)
  const t = Math.max(0, tolMm)
  target.halfSize.set(
    Math.max(1, source.halfSize.x - t),
    Math.max(1, source.halfSize.y - t),
    Math.max(1, source.halfSize.z - t),
  )
  return target
}

function isCenterOverUnit(
  appliancePos: Vector3,
  applianceQuat: Quaternion,
  appliance: SmallCountertopAppliance,
  unit: LinkedItem,
): boolean {
  const unitPose = decomposeOrigin(unit.matrix)
  const center = getObbCenter(
    appliancePos,
    applianceQuat,
    appliance.width,
    appliance.height || 0,
    appliance.thickness || 0,
  )
  const local = center
    .clone()
    .sub(unitPose.position)
    .applyQuaternion(unitPose.quaternion.clone().invert())
  return (
    local.x >= -HOST_XZ_MARGIN_MM &&
    local.x <= (unit.width || 0) + HOST_XZ_MARGIN_MM &&
    local.z >= -HOST_XZ_MARGIN_MM &&
    local.z <= (unit.thickness || 0) + HOST_XZ_MARGIN_MM
  )
}

function footprintsOverlapXZ(
  appliancePos: Vector3,
  applianceQuat: Quaternion,
  appliance: SmallCountertopAppliance,
  unit: LinkedItem,
): boolean {
  fillOBB(
    appliancePos,
    applianceQuat,
    appliance.width,
    appliance.height || 0,
    appliance.thickness || 0,
    probeOBB,
  )
  probeOBB.halfSize.y = 1e6
  const unitPose = decomposeOrigin(unit.matrix)
  fillOBB(
    unitPose.position,
    unitPose.quaternion,
    unit.width,
    unit.height || 0,
    unit.thickness || 0,
    neighborOBB,
  )
  neighborOBB.halfSize.y = 1e6
  return probeOBB.intersectsOBB(neighborOBB)
}

export function findHostUnitUnderAppliance(
  appliance: SmallCountertopAppliance,
  items: Map<string, SceneObjects>,
): LinkedItem | undefined {
  const pose = decomposeOrigin(appliance.matrix)
  let best: LinkedItem | undefined
  let bestDist = Infinity
  items.forEach((candidate) => {
    if (!isFurnitureHostUnit(candidate)) return
    if (!footprintsOverlapXZ(pose.position, pose.quaternion, appliance, candidate)) return
    const dist = Math.abs(getUnitSupportTopYMm(candidate, items) - (appliance.y ?? 0))
    if (dist < bestDist) {
      bestDist = dist
      best = candidate
    }
  })
  return best
}

function findCenterHostUnit(
  appliancePos: Vector3,
  applianceQuat: Quaternion,
  appliance: SmallCountertopAppliance,
  items: Map<string, SceneObjects>,
): LinkedItem | undefined {
  let best: LinkedItem | undefined
  let bestDist = Infinity
  items.forEach((candidate) => {
    if (
      !isFurnitureHostUnit(candidate) ||
      !isCenterOverUnit(appliancePos, applianceQuat, appliance, candidate)
    ) {
      return
    }
    const dist = Math.abs((appliancePos.y ?? 0) - getUnitSupportTopYMm(candidate, items))
    if (dist < bestDist) {
      bestDist = dist
      best = candidate
    }
  })
  return best
}

export function computeSeatedAppliancePose(
  appliance: SmallCountertopAppliance,
  unit: LinkedItem,
  items: Map<string, SceneObjects>,
  options?: { centerOnUnit?: boolean },
): { position: Vector3; quaternion: Quaternion } {
  const seatedY = getSeatedApplianceYMm(appliance, unit, items)
  const appliancePose = decomposeOrigin(appliance.matrix)
  const unitPose = decomposeOrigin(unit.matrix)

  if (!options?.centerOnUnit) {
    appliancePose.position.y = seatedY
    return appliancePose
  }

  const quaternion = unitPose.quaternion.clone()
  const unitMid = new Vector3(unit.width / 2, 0, (unit.thickness || 0) / 2).applyQuaternion(
    unitPose.quaternion,
  )
  const applianceHalf = new Vector3(
    appliance.width / 2,
    0,
    (appliance.thickness || 0) / 2,
  ).applyQuaternion(quaternion)
  const position = unitPose.position.clone().add(unitMid).sub(applianceHalf)
  position.y = seatedY
  return { position, quaternion }
}

function findVolumePenetration(
  appliance: SmallCountertopAppliance,
  position: Vector3,
  quaternion: Quaternion,
  items: Map<string, SceneObjects>,
): boolean {
  fillOBB(
    position,
    quaternion,
    appliance.width,
    appliance.height || 0,
    appliance.thickness || 0,
    probeOBB,
  )
  shrinkUniform(probeOBB, CONTACT_TOLERANCE_MM, probeCore)

  for (const candidate of items.values()) {
    if (candidate.id === appliance.id) continue
    if (isFurnitureHostUnit(candidate)) {
      const pose = decomposeOrigin(candidate.matrix)
      const solidHeight = Math.max(
        1,
        getUnitSupportTopYMm(candidate, items) - (candidate.y ?? 0) - SEAT_SINK_MM,
      )
      fillOBB(
        pose.position,
        pose.quaternion,
        candidate.width,
        solidHeight,
        candidate.thickness || 0,
        neighborOBB,
      )
    } else if (isSmallCountertopAppliance(candidate)) {
      const pose = decomposeOrigin(candidate.matrix)
      fillOBB(
        pose.position,
        pose.quaternion,
        candidate.width,
        candidate.height || 0,
        candidate.thickness || 0,
        neighborOBB,
      )
    } else {
      continue
    }
    shrinkUniform(neighborOBB, CONTACT_TOLERANCE_MM, neighborCore)
    if (probeCore.intersectsOBB(neighborCore)) return true
  }
  return false
}

export function doesPosePenetrateSolids(
  appliance: SmallCountertopAppliance,
  position: Vector3,
  quaternion: Quaternion,
  items: Map<string, SceneObjects>,
): boolean {
  return findVolumePenetration(appliance, position, quaternion, items)
}

/** Si `proposed` penetra, busca un XZ cercano libre (centro de escena / hueco). */
export function findNonPenetratingPosition(
  appliance: SmallCountertopAppliance,
  items: Map<string, SceneObjects>,
  proposed: Vector3,
  quaternion: Quaternion,
): Vector3 {
  if (!findVolumePenetration(appliance, proposed, quaternion, items)) return proposed.clone()
  const radii = [200, 400, 700, 1100, 1600, 2200, 3000]
  const steps = 8
  for (const radius of radii) {
    for (let i = 0; i < steps; i++) {
      const angle = (i / steps) * Math.PI * 2
      const sample = proposed.clone()
      sample.x += Math.cos(angle) * radius
      sample.z += Math.sin(angle) * radius
      if (!findVolumePenetration(appliance, sample, quaternion, items)) return sample
    }
  }
  return proposed.clone()
}

function isSeatedOnUnit(
  appliance: SmallCountertopAppliance,
  position: Vector3,
  unit: LinkedItem,
  items: Map<string, SceneObjects>,
): boolean {
  return (
    Math.abs(position.y - getSeatedApplianceYMm(appliance, unit, items)) <= SEATED_Y_TOLERANCE_MM
  )
}

/**
 * Solo cambia Y si ya iba sentado en un tope (sigue en el mismo o pasa a otro).
 * Acercarse por un lateral no sube a la encimera.
 */
function resolveDragY(
  appliance: SmallCountertopAppliance,
  position: Vector3,
  quaternion: Quaternion,
  items: Map<string, SceneObjects>,
  lastPos: Vector3,
  lastHostId?: string,
): number {
  const host = findCenterHostUnit(position, quaternion, appliance, items)
  const lastHost =
    lastHostId && isFurnitureHostUnit(items.get(lastHostId))
      ? (items.get(lastHostId) as LinkedItem)
      : undefined
  const comingFromSeat = Boolean(lastHost && isSeatedOnUnit(appliance, lastPos, lastHost, items))

  if (comingFromSeat) {
    if (host) return getSeatedApplianceYMm(appliance, host, items)
    return lastPos.y
  }
  return Math.max(0, position.y)
}

function isFreePose(
  appliance: SmallCountertopAppliance,
  position: Vector3,
  quaternion: Quaternion,
  items: Map<string, SceneObjects>,
  lastPos: Vector3,
  lastHostId?: string,
): Vector3 | null {
  const next = position.clone()
  next.y = resolveDragY(appliance, next, quaternion, items, lastPos, lastHostId)
  if (findVolumePenetration(appliance, next, quaternion, items)) return null
  return next
}

function seatedHostId(
  appliance: SmallCountertopAppliance,
  position: Vector3,
  quaternion: Quaternion,
  items: Map<string, SceneObjects>,
): string | undefined {
  const host = findCenterHostUnit(position, quaternion, appliance, items)
  if (!host || !isSeatedOnUnit(appliance, position, host, items)) return undefined
  return host.id
}

export function beginSmallApplianceDrag(appliance: SmallCountertopAppliance, items: Map<string, SceneObjects>) {
  const pose = decomposeOrigin(appliance.matrix)
  lastValidById.set(appliance.id, {
    x: pose.position.x,
    y: pose.position.y,
    z: pose.position.z,
    quaternion: pose.quaternion.clone(),
    hostId: seatedHostId(appliance, pose.position, pose.quaternion, items),
  })
}

export function ensureSmallApplianceDrag(appliance: SmallCountertopAppliance, items: Map<string, SceneObjects>) {
  if (lastValidById.has(appliance.id)) return
  beginSmallApplianceDrag(appliance, items)
}

export function endSmallApplianceDrag(applianceId: string) {
  lastValidById.delete(applianceId)
}

/**
 * Arrastre libre: no salta a la encimera al rozar un lateral.
 * Si la pose entra en un mueble se conserva la última válida.
 * La Y solo se recalcula al pasar de un tope a otro (ya sentado).
 */
export function resolveSmallApplianceFreeDrag(
  appliance: SmallCountertopAppliance,
  items: Map<string, SceneObjects>,
  proposed: Vector3,
): { position: Vector3; quaternion: Quaternion } | null {
  const stored = lastValidById.get(appliance.id)
  const current = decomposeOrigin(appliance.matrix)
  const lastPos = stored
    ? new Vector3(stored.x, stored.y, stored.z)
    : current.position.clone()
  const quaternion = stored?.quaternion.clone() ?? current.quaternion.clone()
  const lastHostId = stored?.hostId

  const tryAt = (pos: Vector3) =>
    isFreePose(appliance, pos, quaternion, items, lastPos, lastHostId)

  const commit = (pos: Vector3) => {
    lastValidById.set(appliance.id, {
      x: pos.x,
      y: pos.y,
      z: pos.z,
      quaternion: quaternion.clone(),
      hostId: seatedHostId(appliance, pos, quaternion, items),
    })
    return { position: pos, quaternion }
  }

  const direct = tryAt(proposed)
  if (direct) return commit(direct)

  let lastFree = tryAt(lastPos)
  if (!lastFree) lastFree = lastPos.clone()

  let lo = 0
  let hi = 1
  let best = lastFree
  for (let i = 0; i < 16; i++) {
    const mid = (lo + hi) * 0.5
    const sample = new Vector3().lerpVectors(lastPos, proposed, mid)
    const free = tryAt(sample)
    if (free) {
      lo = mid
      best = free
    } else {
      hi = mid
    }
  }

  const axisTries = [
    new Vector3(proposed.x, lastPos.y, lastPos.z),
    new Vector3(lastPos.x, lastPos.y, proposed.z),
    new Vector3(proposed.x, lastPos.y, proposed.z),
  ]
  let bestScore = best.distanceToSquared(proposed)
  for (const tryPos of axisTries) {
    const free = tryAt(tryPos)
    if (!free) continue
    const score = free.distanceToSquared(proposed)
    if (score < bestScore) {
      bestScore = score
      best = free
    }
  }

  return commit(best)
}

/** @deprecated Usar resolveSmallApplianceFreeDrag */
export function resolveSmallApplianceDrag(
  appliance: SmallCountertopAppliance,
  items: Map<string, SceneObjects>,
): { position: Vector3; quaternion: Quaternion; wrote: boolean } {
  const pose = decomposeOrigin(appliance.matrix)
  const resolved = resolveSmallApplianceFreeDrag(appliance, items, pose.position)
  if (!resolved) return { ...pose, wrote: false }
  const wrote =
    Math.abs(resolved.position.x - pose.position.x) > 0.5 ||
    Math.abs(resolved.position.y - pose.position.y) > 0.5 ||
    Math.abs(resolved.position.z - pose.position.z) > 0.5
  return { ...resolved, wrote }
}
